import { ethers } from "ethers";
import type { PrivateFeePolicy } from "../base-app/hooks/usePrivateFeePolicy";
import type {
  FlatJoinSplitV2WithdrawParams,
  FlatJoinSplitV2OperationResult,
} from "./operationTypes";
import { computeNPK } from "../joinsplit/keys";
import { BN254_SCALAR_FIELD, flatFieldHex } from "./notes";
import { preparePrivateFeeSpend } from "./privateFees";
import {
  decodePrivateSwap,
  PRIVATE_SWAP_INTERFACE,
  privateSwapDeployment,
} from "./privateSwapConfig";
import { sendSimulated } from "./settlement";
import { getBaseReadProvider } from "../utils/base-read-provider";
import { requestPrivateFees } from "../utils/relayer-client";
import { withFlatJoinSplitV2WalletLock } from "./walletLock";
import { EVM_CONFIG } from "../config";

export interface PrivateSwapFeeQuote {
  chainId: number;
  pool: string;
  coordinator: string;
  outputPool: string;
  inputToken: string;
  outputToken: string;
  amount: string;
  inputAmount: string;
  protocolFee: string;
  partnerFee: string;
  gasFee: string;
  feeTier: number;
  amountOut: string;
  quotedAt: number;
  policy: PrivateFeePolicy;
}
export async function executePrivateFeeSwap(
  params: FlatJoinSplitV2WithdrawParams & {
    swapQuote: PrivateSwapFeeQuote;
    slippageBps: number;
  },
): Promise<FlatJoinSplitV2OperationResult> {
  try {
    return await withFlatJoinSplitV2WalletLock({
      chainId: EVM_CONFIG.CHAIN_ID,
      poolAddress: EVM_CONFIG.CONTRACTS.SHIELDED_POOL_JOINSPLIT,
      accountAddress: await params.signer.getAddress(),
      signal: params.signal,
      run: async () => {
        const q = params.swapQuote,
          deployment = privateSwapDeployment();
        if (
          !deployment ||
          q.coordinator !== deployment.coordinator ||
          q.outputPool !== deployment.pool ||
          q.chainId !== 84532 ||
          q.inputToken !== ethers.getAddress(params.tokenAddress) ||
          BigInt(q.amount) !== params.amount ||
          !Number.isInteger(params.slippageBps) ||
          params.slippageBps < 1 ||
          params.slippageBps > 100 ||
          Date.now() - q.quotedAt > 120000
        )
          throw Error("Refresh the swap quote before continuing");
        const random =
          (BigInt(ethers.hexlify(ethers.randomBytes(32))) %
            (BN254_SCALAR_FIELD - 1n)) +
          1n;
        const seed = {
          recipientMpk: params.spendingKeys.mpk,
          random,
          outputNpk: flatFieldHex(
            await computeNPK(params.spendingKeys.mpk, random),
          ),
          outputTokenAddress: q.outputToken,
          outputPoolAddress: q.outputPool,
        };
        const swap = {
          outputToken: q.outputToken,
          minimumOut: (
            (BigInt(q.amountOut) * BigInt(10000 - params.slippageBps)) /
            10000n
          ).toString(),
          outputNpk: seed.outputNpk,
          deadline: Math.floor(Date.now() / 1000) + 900,
          fee: q.feeTier,
          salt: ethers.hexlify(ethers.randomBytes(32)),
        };
        const prepared = await preparePrivateFeeSpend(params, 1, {
          pool: q.pool,
          policy: q.policy,
          swap,
          seed,
        });
        const authorized = prepared.authorized.swap?.transaction;
        const decoded = authorized && decodePrivateSwap(authorized.data);
        if (
          !authorized ||
          !decoded ||
          authorized.to !== deployment.coordinator ||
          BigInt(authorized.value) !== 0n ||
          authorized.chainId !== 84532 ||
          decoded.recovery
        )
          throw Error("Swap authorization did not match");
        const expected = PRIVATE_SWAP_INTERFACE.encodeFunctionData("execute", [
          prepared.quote.request!.swapRoute,
          prepared.leg,
          PRIVATE_SWAP_INTERFACE.decodeFunctionData("execute", authorized.data)
            .permit,
          ...(prepared.recovery ? [prepared.recovery.records] : []),
        ]);
        if (expected.toLowerCase() !== authorized.data.toLowerCase())
          throw Error("Swap authorization changed the transaction");
        const transaction = {
          to: authorized.to,
          data: authorized.data as `0x${string}`,
          value: 0n,
          chainId: prepared.chainId,
        };
        const settled = await sendSimulated(
          params.signer,
          prepared.provider,
          transaction,
          prepared.pool,
          prepared.ownedOrSent,
          params.onLog,
          prepared.accountAddress,
          (signed) => prepared.journal(signed),
          true,
          params.signal,
          seed,
          getBaseReadProvider(),
          () =>
            requestPrivateFees("submit", prepared.authorizationInput, {
              pool: prepared.pool,
              signal: params.signal,
            }),
        );
        return {
          success: true,
          txHash: settled.txHash,
          journalId: settled.journalId,
          spentNotes: prepared.selected.map((n) => n.note),
          outputNotes: settled.outputs,
        };
      },
    });
  } catch (error: any) {
    return {
      success: false,
      spentNotes: [],
      outputNotes: [],
      error: error?.message || "Swap failed",
      txHash: error?.txHash,
      journalId: error?.journalId,
    };
  }
}
